// ═══════════════════════════════════════════════════════════════════════════════
// Browser / device detection helpers (UI fallback only)
// ═══════════════════════════════════════════════════════════════════════════════

/** Google Chrome on Play Store — used for Android Web Push guidance only. */
export const ANDROID_CHROME_PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.android.chrome';

/** Below this Chrome major version on Android, suggest updating for reliable Web Push. */
const ANDROID_CHROME_MIN_MAJOR_FOR_PUSH = 120;

const isSafariLikeUa = (userAgent: string): boolean => {
  const hasSafariToken = /Safari/i.test(userAgent);
  if (!hasSafariToken) {
    return false;
  }
  return !/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/i.test(userAgent);
};

export const isIPhoneSafariBrowser = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const userAgent = navigator.userAgent || '';
  const isIPhone = /iPhone/i.test(userAgent);
  if (!isIPhone) {
    return false;
  }
  return isSafariLikeUa(userAgent);
};

/** Android phone/tablet UA (excludes iOS / desktop). */
export const isAndroidDevice = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const userAgent = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return false;
  }
  return /Android/i.test(userAgent);
};

/**
 * Google Chrome major version on Android only.
 * Returns null for Samsung Internet / Edge / Firefox / Opera / non-Android.
 */
export const getAndroidChromeMajorVersion = (): number | null => {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const userAgent = navigator.userAgent || '';
  if (!/Android/i.test(userAgent) || /iPhone|iPad|iPod/i.test(userAgent)) {
    return null;
  }
  if (/SamsungBrowser|EdgA|OPR|Opera|Firefox|FxiOS|CriOS/i.test(userAgent)) {
    return null;
  }
  const match = userAgent.match(/Chrome\/(\d+)/i);
  if (!match) {
    return null;
  }
  const major = Number(match[1]);
  if (!Number.isFinite(major) || major <= 0) {
    return null;
  }
  return major;
};

export const isWebPushApiSupported = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

/**
 * Android-only: browser lacks Web Push APIs, or Google Chrome major version is below 120.
 * Never true on iOS or desktop — do not suggest installing Android Chrome there.
 */
export const shouldSuggestChromeOnAndroidForPush = (): boolean => {
  if (!isAndroidDevice()) {
    return false;
  }
  if (!isWebPushApiSupported()) {
    return true;
  }
  const chromeMajor = getAndroidChromeMajorVersion();
  if (chromeMajor === null) {
    return false;
  }
  return chromeMajor < ANDROID_CHROME_MIN_MAJOR_FOR_PUSH;
};
